import { Form, Formik } from 'formik';
import {
  Button,
  Input,
  LoadingSpinner,
  Modal,
} from '@rabbit/elements/shared-components';
import * as Yup from 'yup';
import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getConsumerURL, getPersonaData } from 'apps/sage/src/utils/helpers';
import { CaseflowContext } from 'apps/sage/src/context/CaseflowContext';
import { MANDRILL_TEMPLATES } from '@rabbit/bizproc/react';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { AppContext } from '@rabbit/app-context';
import { DTRepairer_Public, FBD_Consumer_Private } from '@rabbit/data/types';
import { CORPORATE } from '@rabbit/sage/utils/consts.ts';

export interface ContactCustomerModalProps {
  messageCustomer?: boolean;
  customeMessage?: string;
}

interface FormValueShape {
  comment_to_customer: string;
  internal_comment: string;
}

const initialValues: FormValueShape = {
  comment_to_customer: '',
  internal_comment: '',
};

const validationSchema = Yup.object().shape({
  comment_to_customer: Yup.string()
    .trim()
    .required(t('message.enterMessage'))
    .test('remove-html-tags', 'Please insert a message.', (value) => {
      const div = document.createElement('div');
      div.innerHTML = value || '';
      return !value || div.textContent?.trim() !== '';
    }),
  internal_comment: Yup.string().trim(),
});

export function ContactCustomerModal({
  messageCustomer,
  customeMessage
}: ContactCustomerModalProps) {
  const { t } = useTranslation();
  const { tenantInfo } = useContext(AppContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInternalComment, setShowInternalComment] = useState(false);
  const [operatingPersonaInfo, setOperatingPersonaInfo] = useState({
    name: '',
    email: '',
  });

  const {
    caseFacts,
    alterCaseFacts,
    alterCasePublicEmail,
    executeAction,
    moveSpotlight,
    setShowModal,
    operatingPersona,
    consumerPersonaData,
    caseId,
  } = useContext(CaseflowContext) || {};

  if (
    !setShowModal ||
    !executeAction ||
    !alterCaseFacts ||
    !alterCasePublicEmail ||
    !moveSpotlight ||
    !operatingPersona ||
    !consumerPersonaData ||
    !caseFacts ||
    !caseId
  )
    return <LoadingSpinner size={'xs'} />;
  
    useEffect(() => {
      async function fetchPersonaData() {
        if (operatingPersona) {
          try {
            const operatingPersonaData = await getPersonaData(operatingPersona);
            if (operatingPersonaData?.[0]) {
              setOperatingPersonaInfo({
                name: (operatingPersonaData[0] as DTRepairer_Public)?.name ?? '',
                email: operatingPersonaData[0]?.email ?? '',
              });
            }
          } catch (error) {
            console.error('Error fetching persona data:', error);
          }
        }
      }
      fetchPersonaData();
    }, [operatingPersona]);
  const consumer_persona_id = caseFacts?.consumer_persona_id;

  const onSubmit = async (values: FormValueShape) => {
    const { internal_comment, comment_to_customer } = values;
    setIsSubmitting(true);
    const consumerData = await FBD_Consumer_Private.get(
      consumer_persona_id ?? ''
    );
    // NOTE: I don't think this distinction is actually doing anything...is this supposed to be a conditional?
    // to send email or just do a comment within the app?
    messageCustomer
      ? executeAction('message_customer')
      : executeAction('contact_customer_generic');

    try {
      const factsToAlter = {
        ...(internal_comment && {
          internal_comment: {
            comment: internal_comment,
            author: operatingPersona,
          },
        }),
        comment_to_customer: comment_to_customer,
      };
      

      const to = caseFacts?.consumer_email ?? consumerPersonaData.email,
        from = operatingPersonaInfo.email || tenantInfo?.emailInfo?.emailSender || '',
        mainTemplate = MANDRILL_TEMPLATES.MAIN_HEADER_FOOTER_DEFAULT_WITHOUT_IWARRANTY_LOGO,
        template = MANDRILL_TEMPLATES.BODY_BLANK_V2,
        business_name = operatingPersonaInfo.name || tenantInfo?.name || '',
        claim_id = caseId,
        first_name = caseFacts?.consumer_name ?? '',
        product_name = caseFacts.consumer_holding_name ?? '',
        subject = t('email.subject.newMessage', {
          business_name,
          product_name,
        }),
        link_to_claim =
          consumerData?.consumer_type === CORPORATE
            ? `${window.location.origin}/claims/${caseId}`
            : `${getConsumerURL()}/repairs/${caseId}`;
      if (Object.keys(factsToAlter).length > 0) {
        await alterCaseFacts(factsToAlter);
        await alterCasePublicEmail({
          context: 'message_customer',
          from,
          to,
          subject,
          template,
          mainTemplate,
          substitutions: {
            subject,
            business_name,
            claim_id,
            first_name,
            product_name,
            comment_to_customer,
            link_to_claim,
          },
          shallBeSentViaBackend: true,
        });
      }
      setShowModal(false);
      toast.success(t('general.emailSentToUser'));
    } catch (err) {
      console.log(err);
      toast.error(t('message.somethingWentWrongPleaseTryAgain'));
    }
  };

  return (
    <Modal
      settings={{
        title: t('general.contactCustomer'),
        headerBackground: true,
        handleClose: () => setShowModal(false),
      }}
      kind="generic"
      isLoading={isSubmitting}
      className="max-h-[768px] w-full max-w-[1024px] overflow-y-auto"
    >
      <Formik
        initialValues={{...initialValues, comment_to_customer: customeMessage || ''}}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ values, errors }) => {
          return (
            <Form className="mt-5 flex flex-col px-4">
              <Input
                type="rich-text"
                label={`${t('general.commentsToCustomer')}*`}
                name="comment_to_customer"
                settings={{
                  id: 'comment_to_customer',
                  hint: '*required',
                }}
              />
              {!showInternalComment && (
                <div className="mt-4">
                  <Button
                    kind="outline"
                    type="button"
                    className="w-full"
                    onClick={() => setShowInternalComment(true)}
                  >
                    {t('message.addInternalComment')}
                  </Button>
                </div>
              )}
              {showInternalComment && (
                <div className="mt-3">
                  <Input
                    type="rich-text"
                    label={t('general.internalComment')}
                    name="internal_comment"
                    settings={{
                      id: 'internal_comment',
                      placeholder: '',
                      allowSpecialCharacter: true,
                    }}
                  />
                </div>
              )}
              <div className="mt-8 flex gap-8">
                <Button kind="primary" type="submit" loading={isSubmitting}>
                  {t('general.notifyCustomer')}
                </Button>
                <Button
                  kind="outline_red"
                  type="submit"
                  onClick={() => setShowModal(false)}
                >
                  {t('general.cancel')}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
}

export default ContactCustomerModal;
